import {
  OmniStyleElement,
  OmniIconElement,
  OmniTooltipElement,
  OmniElement,
  styleMap,
  html,
  css,
  ref,
  createRef,
} from 'omni-ui';
import { produce } from 'immer';
import '../campaign-assist-field.js';
import '../campaign-draggable-chips.js';
import '../campaign-input-card.js';

OmniStyleElement.register();
OmniIconElement.register();
OmniTooltipElement.register();

export class CampaignThoughtLeaderStepOne extends OmniElement {
  /**
   * Attach Objects Lit Properties
   * @property {Object} context - Values passed in from wrapper/router
   * @property {string} moduleTitle - Title text for module block
   * @property {Object} areas - A state value that saves the area chips of the visualization
   * @property {Object[]} areas.needStates - An array of chip objects for the needStates area
   * @property {Object[]} areas.messages - An array of chip objects for the messages area
   * @property {Function} assistTemplateContext - Function to help resolve templates in assist prompt
   */
  static get properties() {
    return {
      context: { type: Object },
      moduleTitle: { type: String },
      textAreaContent: { type: String },
      savedData: { type: Object },
      areas: { type: Object },
      tempAddInputValue: { type: String },
      backgroundColors: { type: Object },
      assistTemplateContext: { type: Function },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .omni .box {
          padding: 2.143rem 2.86rem 1.714rem;
          margin-bottom: 2.143rem;
        }

        .viz-grid {
          height: 23rem; /* from figma */
          max-height: 80vh;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          z-index: 4;
          grid-template-areas: 'needs message';
          margin-top: 3rem;
        }

        .viz-grid > div {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .viz-controls {
          position: absolute;
          display: flex;
          align-items: center;
        }

        .viz-label {
          letter-spacing: 0.02rem;
          color: rgb(var(--rgb-core-dark));
          margin-right: 1.1428rem;
        }
        .viz-needStates {
          grid-area: needs;
          border-right: 1px solid rgba(var(--rgb-border));
        }
        .viz-messages {
          grid-area: message;
        }
        .viz-needStates .viz-controls {
          top: 1.785rem;
          left: 2.142rem;
          z-index: 3;
        }
        .viz-messages .viz-controls {
          top: 1.785rem;
          right: 2.142rem;
          z-index: 3;
        }

        omni-tooltip {
          z-index: 4; /* higher to avoid chip/area conflicts */
          margin-left: 0.5rem; /* always appears to right of label */
        }

        .textarea-field-row {
          display: flex;
          justify-content: space-between;
          margin: 2.142rem 0 0 0;
        }
        .textarea-label {
          font-size: 0.785rem;
          margin-bottom: 1.07rem;
          color: rgb(var(--rgb-core-dark));
          letter-spacing: 0.02rem;
        }
        .textarea-field {
          flex-basis: calc((100% / 3) - 1.07rem);
          flex-grow: 0;
          flex-shrink: 0;
        }

        textarea.textarea:read-only,
        textarea.textarea:read-only:is(:focus, :active, :hover) {
          resize: none;
          background-color: transparent;
          border: none;
          box-shadow: none;
          color: rgb(var(--rgb-core-dark));
        }
        .title.module-title {
          margin-bottom: 10rem;
        }
        .main-wrapper {
          position: relative;
          display: flex;
          flex-direction: column;
        }
        .tile {
          width: 33%;
          align-self: center;
          position: absolute;
          z-index: 5;
          cursor: pointer;
        }
        .thought-input {
          user-select: none;
          -webkit-user-select: none;
        }
        campaign-input-card::part(tooltip) {
          margin-bottom: 0rem !important;
          margin-right: 0rem !important;
        }

        /**
         * TODO: revisit this z-index battle and maybe have input-card append to the body
         */
        campaign-input-card::part(toggle-open-button) {
          z-index: 3;
        }
        campaign-input-card::part(module-container) {
          z-index: 4;
        }

        campaign-input-card::part(input-container) {
          margin-bottom: 1rem;
        }

        campaign-input-card::part(input-length-indicator) {
          margin-top: 0.285rem;
          font-size: 0.8rem;
          color: rgb(var(--rgb-core-medium));
        }
        campaign-input-card::part(input-length-max) {
          color: rgb(var(--rgb-core-dark));
        }

        campaign-draggable-chips::part(chip-dragging) {
          z-index: 4;
        }
      `,
    ];
  }

  constructor() {
    super();
    this.areas = {
      needStates: [],
      messages: [],
    };
    this.tempAddInputValue = '';
    this.outputValue = '';
    this.textAreaContent = '';
    this.tileRef = createRef();
    this.mainWrapperRef = createRef();
  }

  /**
   * Sets up event listener for the 'recommendation-update' event when the component is connected.
   * @listens event:campaign-input-card-close and calls {@link #resetTempAddInputValue}.
   */
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(
      'campaign-input-card-close',
      this.#resetTempAddInputValue,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(
      'campaign-input-card-close',
      this.#resetTempAddInputValue,
    );
  }

  firstUpdated() {
    if (Object.keys(this.savedData || {})?.length) {
      this.areas = this.savedData.areas;
      this.outputValue = this.savedData.output;
      this.textAreaContent = this.savedData.textarea;
    }
  }

  #resetTempAddInputValue = () => {
    this.tempAddInputValue = '';
  };

  /**
   * @description method that handles passing the data up to the parent component to be saved.
   */
  #handleChildChanges = () => {
    this.dispatchNewEvent('thought-change', {
      detail: {
        output: this.outputValue,
        areas: this.areas,
        textarea: this.textAreaContent,
      },
    });
  };

  /**
   * Adds an item to draggable area containers with the given key and text.
   * Calls {@link #resetTempAddInputValue} and {@link #handleChange}.
   * @param {string} key - The key to add the item to.
   * @param {string} text - The text of the item to add.
   */
  #addItem = (key, text) => {
    this.areas = produce(this.areas, draft => {
      draft[key].push({ text, top: '50%', left: '15%' });
    });
    this.#resetTempAddInputValue();
    this.#handleChildChanges();
  };

  /**
   * Removes an item from draggable area containers with the given key and index.
   * Calls {@link #handleChange}.
   * @param {string} key - The key to remove the item from.
   * @param {number} ind - The index of the item to remove.
   */
  #removeItem = (key, ind) => {
    this.areas = produce(this.areas, draft => {
      draft[key].splice(ind, 1);
    });
    this.#handleChildChanges();
  };

  /**
   * Updates an item in draggable area containers with the given key, index, and new values.
   * Calls {@link #handleChange}.
   * @param {string} key - The key to update the item in.
   * @param {number} ind - The index of the item to update.
   * @param {Object} val - The new values for the item.
   * @param {string} val.top - The new top value for the item.
   * @param {string} val.left - The new left value for the item.
   */
  #updateItem = (key, ind, { top, left }) => {
    this.areas = produce(this.areas, draft => {
      draft[key][ind].top = top;
      draft[key][ind].left = left;
    });
    this.#handleChildChanges();
  };

  /**
   * Renders a tooltip with the given metaTypeKey and metaItemKey.
   * Uses the schema object to retrieve the tooltip position and content for the given keys.
   * @param {string} metaTypeKey - The key for the meta type.
   * @param {string} metaItemKey - The key for the meta item.
   * @returns {TemplateResult} A TemplateResult for the tooltip.
   */
  #renderTooltip = (metaTypeKey, metaItemKey) => html`
    <omni-tooltip
      place=${this.schema[metaTypeKey][metaItemKey].tooltipPosition}
    >
      <omni-icon
        slot="invoker"
        class="is-size-2"
        icon-id="omni:informative:info"
      ></omni-icon>
      <div slot="content">${this.schema[metaTypeKey][metaItemKey].tooltip}</div>
    </omni-tooltip>
  `;

  /**
   * Renders draggable chips for the given key.
   * Uses the areas object to retrieve the chips for the given key and places them accordingly.
   * Passes down the ${@link #removeItem} and ${@link #updateItem} functions to the campaign-draggable-chips component.
   * Passes down the readonly property from the context object.
   * @param {string} key - The key for the areas to render.
   * @returns {TemplateResult} A TemplateResult for the draggable chips.
   */
  #renderItems = key => html`
    <campaign-draggable-chips
      .chips=${this.areas[key]}
      .handleRemoveChip=${ind => {
        this.#removeItem(key, ind);
      }}
      .handleUpdateChip=${(ind, val) => {
        this.#updateItem(key, ind, val);
      }}
      .readonly=${this.context.readonly}
    ></campaign-draggable-chips>
  `;

  /**
   * Renders an input card for the given area by key.
   * @listens campaign-input-card-open event and focuses the input field when it is triggered.
   * @param {string} key - The key for the input card to render.
   * @returns {TemplateResult|null} A TemplateResult for the input card, or null if the context is readonly.
   */
  #renderInputCard = key => {
    const {
      context: { readonly },
      schema,
    } = this;
    if (readonly) return null;

    return html`
      <campaign-input-card
        headerText=${schema.areas[key].label}
        tooltipText="Add Content"
        confirmBtnText="Add"
        toggleBtnText=${schema.areas[key].buttonText}
        alignCard=${schema.areas[key].popoverPosition}
        .handleSubmit=${() => {
          this.#addItem(key, this.tempAddInputValue);
        }}
        .bodyTemplate=${() => html`
          <div part="input-container">
            <campaign-assist-field
              fieldId=${key}
              .fields=${this.context.assistFields}
              .templateContext=${this.assistTemplateContext}
            >
              <input
                required
                class="input input-card-input"
                maxlength="40"
                placeholder="Enter keyword or phrase"
                .value=${this.tempAddInputValue}
                @input=${e => {
                  this.tempAddInputValue = e.target.value?.slice(0, 40);
                }}
              />
            </campaign-assist-field>
            <div
              part=${`input-length-indicator${
                this.tempAddInputValue.length === 40 ? 'input-length-max' : ''
              }`}
            >
              ${this.tempAddInputValue.length}/40
            </div>
          </div>
        `}
        @campaign-input-card-open=${({ target }) => {
          target.shadowRoot.querySelector('.input-card-input')?.focus();
        }}
      ></campaign-input-card>
    `;
  };

  /**
   * Handles the dragging of the main input card tile element.
   * @param {MouseEvent} e - The mouse event object.
   */
  #dragTile = e => {
    const undraggables = Array.from(
      this.tileRef.value.querySelectorAll('h2, textarea, omni-tooltip'),
    );
    // sets the tileShiftX property to the difference between the mouse position and the left edge of the tile element.
    if (undraggables.includes(e.target)) return;
    if (e.screenX === 0) return; // ignore last drag event with 0 screenX
    if (e.type === 'dragstart') {
      this.tileShiftX =
        e.clientX - this.tileRef.value.getBoundingClientRect().left;
      this.tileRef.value.style.opacity = 0.5;
    }
    if (e.type === 'dragend') {
      this.tileShiftX = null;
      this.tileRef.value.style.opacity = 1;
    }
    // calculates the new left edge of the tile element based on the mouse position and the tileShiftX property
    if (this.tileShiftX !== null) {
      let newLeftEdge =
        e.clientX -
        this.mainWrapperRef.value.getBoundingClientRect().left -
        this.tileShiftX;
      const rightEdge =
        this.mainWrapperRef.value.offsetWidth - this.tileRef.value.offsetWidth;

      // clamps the new left edge to be within the bounds of the main wrapper element
      if (newLeftEdge < 0) newLeftEdge = 0;
      if (newLeftEdge > rightEdge) newLeftEdge = rightEdge;
      this.tileRef.value.style.left = `${newLeftEdge}px`;
    }
  };

  render() {
    return html`
      <omni-style>
        <h2 class="title is-3 module-title">${this.moduleTitle}</h2>
        <div class="main-wrapper" ${ref(this.mainWrapperRef)}>
          <omni-tile
            ${ref(this.tileRef)}
            class="tile"
            draggable="true"
            @dragstart="${this.#dragTile}"
            @drag="${this.#dragTile}"
            @dragend="${this.#dragTile}"
          >
            <div class="thought-input m-4">
              <h2 class="mt-4 mb-4 is-flex is-align-items-center">
                ${this.schema?.draggable_input_text}
                <omni-tooltip class="tooltip-icon">
                  <omni-icon
                    slot="invoker"
                    class="is-size-2"
                    icon-id="omni:informative:info"
                  >
                  </omni-icon>
                  <div slot="content">
                    ${this.schema?.draggable_input_tooltip}
                  </div>
                </omni-tooltip>
              </h2>
              <textarea
                class="textarea p-3"
                placeholder="${this.schema?.draggable_placeholder ??
                'Enter wider context'}"
                .value=${this.schema.textAreaSource
                  ? this.context.sharedData[this.schema.textAreaSource]?.value
                  : this.textAreaContent}
                ?readonly=${this.schema.textAreaSource &&
                this.context.sharedData[this.schema.textAreaSource]?.value}
                @change=${e => {
                  this.textAreaContent = e.target.value;
                  this.#handleChildChanges();
                }}
              ></textarea>
            </div>
          </omni-tile>
          <div class="viz-grid">
            ${Object.keys(this.areas).map(
              k => html`
                <div
                  class="area viz-${k}"
                  style=${styleMap({
                    backgroundColor: this.backgroundColors?.[k],
                  })}
                >
                  <div class="viz-controls">
                    <div class="viz-label">
                      ${this.schema.areas[k].label}
                      ${this.#renderTooltip('areas', k)}
                    </div>
                    ${this.#renderInputCard(k)}
                  </div>
                  ${this.#renderItems(k)}
                </div>
              `,
            )}
          </div>
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register(
  'campaign-thought-leader-step-one',
  CampaignThoughtLeaderStepOne,
);
